// KB-979: la gestión de alumnos independientes en el admin queda oculta hasta
// que se lance (crear alumno independiente, filtro y etiqueta en el listado).
// Se prende con VUE_APP_INDEPENDENT_STUDENTS_ENABLED=true en el entorno del build.
export const INDEPENDENT_STUDENTS_ENABLED = process.env.VUE_APP_INDEPENDENT_STUDENTS_ENABLED === 'true';

export const ACCOUNT_ORIGIN = {
  SCHOOL: 'SCHOOL',
  INDEPENDENT: 'INDEPENDENT',
} as const;

// Independiente se decide solo por accountOrigin (contrato C1/C4): un alumno
// con school null puede ser un egresado o dado de baja, no un independiente.
export const isIndependent = (
  user?: { accountOrigin?: string | null } | null,
): boolean => user?.accountOrigin === ACCOUNT_ORIGIN.INDEPENDENT;

// Diagnóstico fuera de v1 para independientes (spec KB-979). loadFinished significa
// "el intento de carga terminó, con éxito o no": mientras no terminó no se sabe el origen
// y no se muestra el panel. Si la carga falló no hay usuario y se trata como SCHOOL.
export const showsPendingDiagnostics = (
  loadFinished: boolean,
  user?: { accountOrigin?: string | null } | null,
): boolean => loadFinished && !isIndependent(user);

// Mismo enum que course.grade (0 = Kínder/preinicial). No es el grade_id de
// coursegrade/findAll/:country, que es un id por país.
export const STUDENT_GRADES: number[] = Array.from({ length: 13 }, (_, i) => i);

export const isValidStudentGrade = (value: unknown): boolean => (
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 12
);

// Contrato C3: zona por defecto por basecountry.id.
export const COUNTRY_DEFAULT_TIME_ZONES: Record<number, string> = {
  1: 'America/Santiago',
  2: 'America/Bogota',
  3: 'America/Chicago',
  4: 'America/Mexico_City',
  5: 'America/Lima',
  13: 'America/Guayaquil',
  16: 'America/Montevideo',
};

export const defaultTimeZoneForCountry = (countryId?: number | null): string | null => (
  countryId != null ? COUNTRY_DEFAULT_TIME_ZONES[countryId] ?? null : null
);

// backend-v2 valida time_zone con la misma lista de Intl.
const supportedTimeZones = (): string[] => (typeof (Intl as any).supportedValuesOf === 'function' ? (Intl as any).supportedValuesOf('timeZone') : []);

export const buildTimeZoneOptions = (names: string[] = supportedTimeZones()): string[] => {
  const featured = Object.values(COUNTRY_DEFAULT_TIME_ZONES);
  const rest = names.filter((name) => !featured.includes(name)).sort();
  return [...featured, ...rest];
};

export const getRequestErrorStatus = (error: any): number | null => (
  error?.response?.status ?? null
);

export const getBackendMessage = (error: any): string | null => {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
};

// El message del backend solo es apto para mostrar en un 400; en un 500 puede ser SQL crudo.
export const updateErrorMessage = (error: any): string | null => (
  getRequestErrorStatus(error) === 400 ? getBackendMessage(error) : null
);

export const createIndependentErrorKey = (error: any): string => {
  const status = getRequestErrorStatus(error);
  if (status === 403) return 'students.independent_forbidden';
  if (status === 400) return 'students.independent_invalid';
  return 'students.create_error';
};

export const buildStudentSearchParams = (filter: Record<string, any>): Record<string, any> => {
  const { onlyIndependent, ...params } = filter;
  if (onlyIndependent !== true) return params;
  return { ...params, schoolId: null, accountOrigin: ACCOUNT_ORIGIN.INDEPENDENT };
};

export const buildStudentUpdatePayload = (
  form: Record<string, any>,
  independent: boolean,
): Record<string, any> => {
  const cleaned: Record<string, any> = {};
  Object.keys(form).forEach((key) => {
    cleaned[key] = form[key] === '' ? null : form[key];
  });
  if (!independent) {
    const { studentGrade, ...schoolPayload } = cleaned;
    return schoolPayload;
  }

  // content-manager (C10b): para INDEPENDENT no se manda colegio/curso y el
  // grado del alumno viaja como studentGrade, no como grade (que es el del curso).
  const {
    school, grade, section, ...rest
  } = cleaned;
  return { ...rest, studentGrade: cleaned.studentGrade ?? null };
};

// Ficha del alumno: si falló findOne el alumno no se encontró; si falló una carga
// posterior (secciones, registro) el alumno sí existe y el mensaje tiene que ser genérico.
export const studentLoadErrorKey = (studentFound: boolean): string => (
  studentFound ? 'students.load_partial_error' : 'students.student_not_found'
);
